/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { ProcessGuide } from '@/types/process-guides.types';

export const GUIDE_CATEGORIES = [
  'sales', 'purchasing', 'inventory', 'products', 'cashBank', 'pos', 'partners', 'admin', 'reports',
] as const;

export const PROCESS_GUIDES: ProcessGuide[] = [
  // === SALES CYCLE ===
  {
    code: 'QUOTE_TO_INVOICE',
    category: 'sales',
    steps: [
      { id: 'create_quote', targetPath: '/venta/quotations/new', nextSteps: ['accept_quote'] },
      { id: 'accept_quote', targetPath: '/venta/quotations', nextSteps: ['convert_to_order'] },
      { id: 'convert_to_order', targetPath: '/venta/quotations', nextSteps: ['create_invoice'] },
      { id: 'create_invoice', targetPath: '/venta/invoices/new', nextSteps: ['collect_payment'] },
      { id: 'collect_payment', targetPath: '/venta/cash-receipts/new' },
    ],
  },
  {
    code: 'DIRECT_INVOICE',
    category: 'sales',
    steps: [
      { id: 'create_invoice', targetPath: '/venta/invoices/new', nextSteps: ['validate_invoice'] },
      { id: 'validate_invoice', targetPath: '/venta/invoices', nextSteps: ['send_invoice'] },
      { id: 'send_invoice', nextSteps: ['collect_payment'] },
      { id: 'collect_payment', targetPath: '/venta/cash-receipts/new' },
    ],
  },
  {
    code: 'CREDIT_NOTE_FROM_INVOICE',
    category: 'sales',
    steps: [
      { id: 'select_invoice', targetPath: '/venta/invoices', nextSteps: ['issue_credit_note'] },
      { id: 'issue_credit_note', nextSteps: ['wallet_credited'] },
      { id: 'wallet_credited' },
    ],
  },
  {
    code: 'VOID_CANCEL_INVOICE',
    category: 'sales',
    steps: [
      { id: 'select_invoice', targetPath: '/venta/invoices', nextSteps: ['void_draft', 'cancel_validated'] },
      { id: 'void_draft' },
      { id: 'cancel_validated' },
    ],
  },
  {
    code: 'RECORD_CUSTOMER_PAYMENT',
    category: 'sales',
    steps: [
      { id: 'select_invoice', targetPath: '/venta/invoices', nextSteps: ['choose_method'] },
      { id: 'choose_method', nextSteps: ['cash_receipt', 'bank_transfer'] },
      { id: 'cash_receipt', targetPath: '/venta/cash-receipts/new' },
      { id: 'bank_transfer', targetPath: '/venta/bank-transfers/new' },
    ],
  },
  {
    code: 'APPLY_WALLET_CREDIT',
    category: 'sales',
    steps: [
      { id: 'open_invoice', targetPath: '/venta/invoices', nextSteps: ['apply_credit'] },
      { id: 'apply_credit', nextSteps: ['confirm_reduction'] },
      { id: 'confirm_reduction' },
    ],
  },

  // === PURCHASING CYCLE ===
  {
    code: 'PO_TO_STOCK',
    category: 'purchasing',
    steps: [
      { id: 'create_po', targetPath: '/venta/purchase-orders/new', nextSteps: ['send_po'] },
      { id: 'send_po', targetPath: '/venta/purchase-orders', nextSteps: ['receive_goods'] },
      { id: 'receive_goods', targetPath: '/venta/goods-receipts/new', nextSteps: ['stock_updated'] },
      { id: 'stock_updated', targetPath: '/venta/stock' },
    ],
  },
  {
    code: 'PARTIAL_GOODS_RECEIPT',
    category: 'purchasing',
    steps: [
      { id: 'open_po', targetPath: '/venta/purchase-orders', nextSteps: ['receive_partial'] },
      { id: 'receive_partial', targetPath: '/venta/goods-receipts/new', nextSteps: ['receive_remaining'] },
      { id: 'receive_remaining', targetPath: '/venta/goods-receipts/new' },
    ],
  },
  {
    code: 'PURCHASE_RETURN_DEBIT',
    category: 'purchasing',
    steps: [
      { id: 'select_receipt', targetPath: '/venta/goods-receipts', nextSteps: ['create_return'] },
      { id: 'create_return', targetPath: '/venta/purchase-returns/new', nextSteps: ['debit_note_created'] },
      { id: 'debit_note_created', targetPath: '/venta/purchasing/debit-notes' },
    ],
  },
  {
    code: 'RECORD_SUPPLIER_PAYMENT',
    category: 'purchasing',
    steps: [
      { id: 'select_po', targetPath: '/venta/purchase-orders', nextSteps: ['create_voucher'] },
      { id: 'create_voucher', targetPath: '/venta/payment-vouchers/new', nextSteps: ['payment_recorded'] },
      { id: 'payment_recorded' },
    ],
  },

  // === INVENTORY ===
  {
    code: 'SETUP_LOCATIONS_BINS',
    category: 'inventory',
    steps: [
      { id: 'create_location', targetPath: '/venta/locations', nextSteps: ['add_bins'] },
      { id: 'add_bins', nextSteps: ['assign_products'] },
      { id: 'assign_products', targetPath: '/venta/products' },
    ],
  },
  {
    code: 'STOCK_ADJUSTMENT',
    category: 'inventory',
    steps: [
      { id: 'select_product', targetPath: '/venta/stock', nextSteps: ['adjust_qty'] },
      { id: 'adjust_qty', targetPath: '/venta/stock/adjust', nextSteps: ['enter_reason'] },
      { id: 'enter_reason', nextSteps: ['confirm_adjustment'] },
      { id: 'confirm_adjustment' },
    ],
  },
  {
    code: 'STOCK_TRANSFER',
    category: 'inventory',
    steps: [
      { id: 'create_transfer', targetPath: '/venta/transfers/new', nextSteps: ['approve_transfer'] },
      { id: 'approve_transfer', targetPath: '/venta/transfers/approvals', nextSteps: ['confirm_receipt'] },
      { id: 'confirm_receipt' },
    ],
  },
  {
    code: 'PHYSICAL_INVENTORY_COUNT',
    category: 'inventory',
    steps: [
      { id: 'create_count', targetPath: '/venta/inventory-counts/new', nextSteps: ['scan_items'] },
      { id: 'scan_items', nextSteps: ['review_variance'] },
      { id: 'review_variance', nextSteps: ['apply_adjustments'] },
      { id: 'apply_adjustments' },
    ],
  },
  {
    code: 'LOW_STOCK_ALERT_SETUP',
    category: 'inventory',
    steps: [
      { id: 'open_product', targetPath: '/venta/products', nextSteps: ['set_reorder_point'] },
      { id: 'set_reorder_point', nextSteps: ['receive_alerts'] },
      { id: 'receive_alerts', targetPath: '/venta/stock/alerts' },
    ],
  },

  // === PRODUCTS & CONFIG ===
  {
    code: 'CREATE_PRODUCT',
    category: 'products',
    steps: [
      { id: 'new_product', targetPath: '/venta/products/new', nextSteps: ['set_category'] },
      { id: 'set_category', nextSteps: ['set_uom_tax'] },
      { id: 'set_uom_tax', nextSteps: ['add_pricing'] },
      { id: 'add_pricing', nextSteps: ['save_product'] },
      { id: 'save_product' },
    ],
  },
  {
    code: 'IMPORT_PRODUCTS',
    category: 'products',
    steps: [
      { id: 'open_import', targetPath: '/venta/products/import', nextSteps: ['map_columns'] },
      { id: 'map_columns', nextSteps: ['validate_data'] },
      { id: 'validate_data', nextSteps: ['run_import'] },
      { id: 'run_import' },
    ],
  },
  {
    code: 'TAX_CONFIGURATION',
    category: 'products',
    steps: [
      { id: 'open_tax', targetPath: '/venta/tax-config', nextSteps: ['create_rates'] },
      { id: 'create_rates', nextSteps: ['assign_to_products'] },
      { id: 'assign_to_products', targetPath: '/venta/products' },
    ],
  },
  {
    code: 'DOCUMENT_NUMBERING',
    category: 'products',
    steps: [
      { id: 'open_numbering', targetPath: '/venta/doc-numbering', nextSteps: ['set_prefix'] },
      { id: 'set_prefix', nextSteps: ['set_sequence'] },
      { id: 'set_sequence' },
    ],
  },
  {
    code: 'MANAGE_FX_RATES',
    category: 'products',
    steps: [
      { id: 'open_fx', targetPath: '/venta/fx-rates', nextSteps: ['add_pair'] },
      { id: 'add_pair', nextSteps: ['set_rate'] },
      { id: 'set_rate', nextSteps: ['apply_to_transactions'] },
      { id: 'apply_to_transactions' },
    ],
  },

  // === CASH & BANK ===
  {
    code: 'SETUP_CASH_REGISTER',
    category: 'cashBank',
    steps: [
      { id: 'create_register', targetPath: '/venta/cash-registers/new', nextSteps: ['set_opening'] },
      { id: 'set_opening', nextSteps: ['start_operations'] },
      { id: 'start_operations', targetPath: '/venta/cash-registers' },
    ],
  },
  {
    code: 'CASH_REGISTER_CLOSE',
    category: 'cashBank',
    steps: [
      { id: 'open_register', targetPath: '/venta/cash-registers', nextSteps: ['review_transactions'] },
      { id: 'review_transactions', nextSteps: ['count_cash'] },
      { id: 'count_cash', nextSteps: ['close_register'] },
      { id: 'close_register' },
    ],
  },
  {
    code: 'SETUP_BANK_ACCOUNT',
    category: 'cashBank',
    steps: [
      { id: 'create_account', targetPath: '/venta/bank-accounts/new', nextSteps: ['set_details'] },
      { id: 'set_details', nextSteps: ['opening_balance'] },
      { id: 'opening_balance' },
    ],
  },
  {
    code: 'BANK_RECONCILIATION',
    category: 'cashBank',
    steps: [
      { id: 'open_recon', targetPath: '/venta/reconciliation', nextSteps: ['import_statement'] },
      { id: 'import_statement', nextSteps: ['auto_match'] },
      { id: 'auto_match', nextSteps: ['manual_match'] },
      { id: 'manual_match', nextSteps: ['close_session'] },
      { id: 'close_session' },
    ],
  },
  {
    code: 'RECORD_BANK_TRANSFER',
    category: 'cashBank',
    steps: [
      { id: 'select_source', targetPath: '/venta/bank-transfers/new', nextSteps: ['select_dest'] },
      { id: 'select_dest', nextSteps: ['enter_amount'] },
      { id: 'enter_amount', nextSteps: ['confirm_transfer'] },
      { id: 'confirm_transfer' },
    ],
  },
  {
    code: 'CASH_RECEIPT_FROM_CUSTOMER',
    category: 'cashBank',
    steps: [
      { id: 'open_receipt', targetPath: '/venta/cash-receipts/new', nextSteps: ['select_customer'] },
      { id: 'select_customer', nextSteps: ['link_invoices'] },
      { id: 'link_invoices', nextSteps: ['record_payment'] },
      { id: 'record_payment' },
    ],
  },
  {
    code: 'PAYMENT_VOUCHER_TO_SUPPLIER',
    category: 'cashBank',
    steps: [
      { id: 'open_voucher', targetPath: '/venta/payment-vouchers/new', nextSteps: ['select_supplier'] },
      { id: 'select_supplier', nextSteps: ['link_po'] },
      { id: 'link_po', nextSteps: ['issue_payment'] },
      { id: 'issue_payment' },
    ],
  },

  // === POS ===
  {
    code: 'POS_COMPLETE_SALE',
    category: 'pos',
    steps: [
      { id: 'select_register', targetPath: '/venta/pos/select-register', nextSteps: ['add_items'] },
      { id: 'add_items', targetPath: '/venta/pos', nextSteps: ['process_payment'] },
      { id: 'process_payment', nextSteps: ['print_receipt'] },
      { id: 'print_receipt' },
    ],
  },
  {
    code: 'POS_OPEN_CLOSE',
    category: 'pos',
    steps: [
      { id: 'open_session', targetPath: '/venta/pos/select-register', nextSteps: ['process_sales'] },
      { id: 'process_sales', targetPath: '/venta/pos', nextSteps: ['close_reconcile'] },
      { id: 'close_reconcile' },
    ],
  },

  // === PARTNERS ===
  {
    code: 'SETUP_CUSTOMER',
    category: 'partners',
    steps: [
      { id: 'create_partner', targetPath: '/venta/partners/new', nextSteps: ['enable_buyer'] },
      { id: 'enable_buyer', nextSteps: ['set_payment_terms'] },
      { id: 'set_payment_terms', nextSteps: ['set_credit_limit'] },
      { id: 'set_credit_limit' },
    ],
  },
  {
    code: 'SETUP_SUPPLIER',
    category: 'partners',
    steps: [
      { id: 'create_partner', targetPath: '/venta/partners/new', nextSteps: ['enable_seller'] },
      { id: 'enable_seller', nextSteps: ['set_payment_terms'] },
      { id: 'set_payment_terms' },
    ],
  },
  {
    code: 'CONVERT_TO_BOTH',
    category: 'partners',
    steps: [
      { id: 'edit_partner', targetPath: '/venta/partners', nextSteps: ['enable_other_role'] },
      { id: 'enable_other_role', nextSteps: ['configure_both'] },
      { id: 'configure_both' },
    ],
  },

  // === ADMIN ===
  {
    code: 'CREATE_USER_ASSIGN_ROLE',
    category: 'admin',
    steps: [
      { id: 'go_users', targetPath: '/venta/admin/users', nextSteps: ['create_user'] },
      { id: 'create_user', targetPath: '/venta/admin/users/new', nextSteps: ['select_role'] },
      { id: 'select_role', nextSteps: ['send_invite'] },
      { id: 'send_invite' },
    ],
  },
  {
    code: 'CUSTOMIZE_ROLE_PERMISSIONS',
    category: 'admin',
    steps: [
      { id: 'go_roles', targetPath: '/venta/admin/roles', nextSteps: ['edit_role'] },
      { id: 'edit_role', nextSteps: ['toggle_permissions'] },
      { id: 'toggle_permissions', nextSteps: ['save_role'] },
      { id: 'save_role' },
    ],
  },

  // === REPORTS ===
  {
    code: 'GENERATE_SALES_REPORT',
    category: 'reports',
    steps: [
      { id: 'open_reports', targetPath: '/venta/reports/sales', nextSteps: ['select_period'] },
      { id: 'select_period', nextSteps: ['apply_filters'] },
      { id: 'apply_filters', nextSteps: ['export_report'] },
      { id: 'export_report' },
    ],
  },
  {
    code: 'CHECK_INVENTORY_VALUATION',
    category: 'reports',
    steps: [
      { id: 'open_valuation', targetPath: '/venta/reports/inventory/valuation', nextSteps: ['review_by_location'] },
      { id: 'review_by_location', nextSteps: ['export_data'] },
      { id: 'export_data' },
    ],
  },
  {
    code: 'CUSTOMER_AGING_REPORT',
    category: 'reports',
    steps: [
      { id: 'open_aging', targetPath: '/venta/reports/financial/aging', nextSteps: ['filter_customer'] },
      { id: 'filter_customer', nextSteps: ['review_overdue'] },
      { id: 'review_overdue' },
    ],
  },
  {
    code: 'CASH_FLOW_ANALYSIS',
    category: 'reports',
    steps: [
      { id: 'open_cashflow', targetPath: '/venta/reports/financial/cash-flow', nextSteps: ['select_period'] },
      { id: 'select_period', nextSteps: ['review_in_out'] },
      { id: 'review_in_out' },
    ],
  },
];
